import Navbar from '../../../Shared/AdminNavbar/Navbar';
import '../../../Layout/Collapse/Collapse.css';
import React, { useEffect, useState, useRef } from "react";
import ReusableTable from "../../../Layout/ReusableTable";
import axios from "axios";
import baseURL from '../../../Apiurs/Apiurls';
import { useNavigate } from 'react-router-dom';
import '../Products/Product.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { FaEdit, FaTrash } from "react-icons/fa";

const Products = () => {
    const [collapsed, setCollapsed] = useState(false);
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    const [mainCategories, setMainCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [products, setProducts] = useState([]);

    const [selectedMainCategory, setSelectedMainCategory] = useState(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);

    const [showMainCategoryDropdown, setShowMainCategoryDropdown] = useState(false);
    const [showSubCategoryDropdown, setShowSubCategoryDropdown] = useState(false);
    const [showProductDropdown, setShowProductDropdown] = useState(false);

    const mainCategoryRef = useRef(null);
    const subCategoryRef = useRef(null);
    const productRef = useRef(null);

    // Loader state
    const [loading, setLoading] = useState(false);

    const handleAddUser = () => {
        navigate('/productdetail');
    };

    const handleStockIn = async (detail_id) => {
        navigate(`/stockin/${detail_id}`);
        await fetchAllDetails(); // refresh after stock in
    };

    const handleStockOut = async (detail_id) => {
        navigate(`/stockout/${detail_id}`);
        await fetchAllDetails(); // refresh after stock out
    };

    const handleViewHistory = (detail_id) => {
        navigate(`/stockhistory/${detail_id}`);
    };

    // ✅ central fetch function
    const fetchAllDetails = async () => {
        try {
            const res = await axios.get(`${baseURL}/api/all-details`);
            setData(res.data);
            setFilteredData(res.data);
        } catch (err) {
            console.error("Error fetching data:", err);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (mainCategoryRef.current && !mainCategoryRef.current.contains(event.target)) {
                setShowMainCategoryDropdown(false);
            }
            if (subCategoryRef.current && !subCategoryRef.current.contains(event.target)) {
                setShowSubCategoryDropdown(false);
            }
            if (productRef.current && !productRef.current.contains(event.target)) {
                setShowProductDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        fetchAllDetails();
        fetchMainCategories();
    }, []);

    const fetchMainCategories = async () => {
        try {
            const res = await axios.get(`${baseURL}/api/main`);
            if (Array.isArray(res.data)) {
                setMainCategories(res.data);
            } else {
                setMainCategories([]);
            }
        } catch (err) {
            console.error("Error fetching main categories:", err.response?.data || err.message);
            setMainCategories([]);
        }
    };

    const fetchSubCategories = async (mainCategoryId) => {
        try {
            const res = await axios.get(`${baseURL}/api/sub-categories-main/${mainCategoryId}`);
            if (Array.isArray(res.data)) {
                setSubCategories(res.data);
            } else {
                setSubCategories([]);
            }
            setProducts([]);
            setSelectedSubCategory(null);
            setSelectedProduct(null);
        } catch (err) {
            console.error("Error fetching sub categories:", err.response?.data || err.message);
            setSubCategories([]);
        }
    };

    const fetchProducts = async (subCategoryId) => {
        try {
            const res = await axios.get(`${baseURL}/api/products-main/${subCategoryId}`);
            if (Array.isArray(res.data)) {
                setProducts(res.data);
            } else {
                setProducts([]);
            }
            setSelectedProduct(null);
        } catch (err) {
            console.error("Error fetching products:", err.response?.data || err.message);
            setProducts([]);
        }
    };

    const handleMainCategorySelect = (category) => {
        setSelectedMainCategory(category);
        fetchSubCategories(category.maincategory_id);
        applyFilters(category.maincategory_id, null, null);
        setShowMainCategoryDropdown(false);
        setShowSubCategoryDropdown(true);
    };

    const handleSubCategorySelect = (subCategory) => {
        setSelectedSubCategory(subCategory);
        fetchProducts(subCategory.subcategory_id);
        applyFilters(
            selectedMainCategory?.maincategory_id,
            subCategory.subcategory_id,
            null
        );
        setShowSubCategoryDropdown(false);
        setShowProductDropdown(true);
    };

    const handleProductSelect = (product) => {
        setSelectedProduct(product);
        applyFilters(
            selectedMainCategory?.maincategory_id,
            selectedSubCategory?.subcategory_id,
            product.product_id
        );
        setShowProductDropdown(false);
    };

    const applyFilters = (mainCategoryId, subCategoryId, productId) => {
        let filtered = data;
        if (mainCategoryId) filtered = filtered.filter(item => item.maincategory_id === mainCategoryId);
        if (subCategoryId) filtered = filtered.filter(item => item.subcategory_id === subCategoryId);
        if (productId) filtered = filtered.filter(item => item.product_id === productId);
        setFilteredData(filtered);
    };

    const clearFilters = () => {
        setSelectedMainCategory(null);
        setSelectedSubCategory(null);
        setSelectedProduct(null);
        setSubCategories([]);
        setProducts([]);
        setFilteredData(data);
        setShowMainCategoryDropdown(false);
        setShowSubCategoryDropdown(false);
        setShowProductDropdown(false);
    };

    // =================== Excel Import/Export ===================
    const handleExportExcel = () => {
        if (filteredData.length === 0) return alert("No data to export");

        const exportData = filteredData.map((item, index) => ({
            SNo: index + 1,
            MainCategory: item.maincategory_name || "-",
            SubCategory: item.subcategory_name || "-",
            Product: item.product_name || "-",
            Model: item.batch || "-",
            Size: item.size || "-",
            Quantity: item.Quantity || 0,
            HSNCode: item.hsncode || "-",
            GSTRate: item.gstrate || "-",
            ListPrice: item.listprice || "-",
            MOQ: item.moq || "-",
            Description: item.description || "-",
            ProductImage: item.product_image || "-",
            TechSpecs: item.tech_specs || "-"
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        ws['!cols'] = [
            { wch: 5 }, { wch: 20 }, { wch: 20 }, { wch: 20 },
            { wch: 15 }, { wch: 10 }, { wch: 10 }, { wch: 15 },
            { wch: 10 }, { wch: 15 }, { wch: 10 }, { wch: 30 },
            { wch: 20 }, { wch: 20 }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Products");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, `Products_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

  const handleImportExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    event.target.value = null;

    setLoading(true);

    try {
        const dataBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(dataBuffer, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const imported = XLSX.utils.sheet_to_json(sheet);

        if (imported.length === 0) {
            alert("No data found in Excel file");
            return;
        }

        const normalizeString = (value) => {
            if (!value) return null;
            return String(value).trim().replace(/\s+/g, " ") || null;
        };

        const mappedData = imported.map((item) => ({
            MainCategory: normalizeString(item.MainCategory || item.maincategory || item["Main Category"]),
            SubCategory: normalizeString(item.SubCategory || item.subcategory || item["Sub Category"]),
            Product: normalizeString(item.Product || item.product),
            Model: normalizeString(item.Model || item.model || item.Batch),
            Description: normalizeString(item.Description || item.description),
            Size: normalizeString(item.Size || item.size),
            HSNCode: normalizeString(item.HSNCode || item.hsncode || item["HSN Code"]),
            GSTRate: item.GSTRate ?? item.gstrate ?? null,
            ListPrice: item.ListPrice ?? item.listprice ?? null,
            MOQ: item.MOQ ?? item.moq ?? null,
            Quantity: item.Quantity ?? item.quantity ?? null,
            ProductImage: item.ProductImage || item.product_image || null,
            TechSpecs: item.TechSpecs || item.tech_specs || null,
        })).filter(row => row.MainCategory || row.SubCategory || row.Product);

        if (mappedData.length === 0) {
            alert("No valid rows found in Excel after mapping");
            return;
        }

        // ✅ First save products
        await axios.post(`${baseURL}/api/import-products`, { products: mappedData });

        // ✅ Then re-fetch updated data
        await fetchAllDetails();

        // ✅ Reset filters after fresh data load
        clearFilters();

        alert("✅ Upload Successful");
    } catch (error) {
        console.error(error);
        alert("Import failed: " + (error.response?.data?.error || error.message || "Unknown error"));
    } finally {
        setLoading(false);
    }
};


    const handleDownloadTemplate = () => {
        const templateColumns = [
            "SNo", "MainCategory", "SubCategory", "Product", "Model", "Description",
            "Size", "HSNCode", "GSTRate", "ListPrice", "MOQ", "Quantity", "ProductImage", "TechSpecs"
        ];

        const ws = XLSX.utils.json_to_sheet([templateColumns.reduce((acc, col) => ({ ...acc, [col]: "" }), {})]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template");
        XLSX.writeFile(wb, "Product_Import_Template.xlsx");
    };

    const handleEdit = (row) => {
        const id = row.id || row.detail_id;
        if (!id) return alert("Unable to edit product: ID not found");
        navigate(`/editproduct/${id}`);
    };

    const handleDelete = async (row) => {
        const id = row.detail_id;
        if (!id) return alert("Unable to delete product: ID not found");
        if (!window.confirm("Are you sure you want to delete this product?")) return;

        try {
            await axios.delete(`${baseURL}/api/product/${id}`);
            await fetchAllDetails(); // ✅ refresh after delete
            alert("Product deleted successfully");
        } catch (err) {
            console.error("Delete failed:", err.message);
            alert(`Failed to delete product: ${err.response?.data?.error || err.message}`);
        }
    };

    const columns = [
        { key: "sno", title: "S.No", render: (row, index) => index + 1 },
        { key: "maincategory_name", title: "Main Category", render: (row) => row.maincategory_name || "-" },
        { key: "subcategory_name", title: "Sub Category", render: (row) => row.subcategory_name || "-" },
        { key: "product_name", title: "Product", render: (row) => row.product_name || "-" },
        { key: "batch", title: "Model", render: (row) => row.batch || "-" },
        { key: "size", title: "Size", render: (row) => row.size || "-" },
        {
            key: "Quantity", title: "Quantity", render: (row) => (
                <div className="d-flex align-items-center gap-2">
                    <span className="fw-bold">{row.Quantity || 0}</span>
                    <div className="btn-group btn-group-sm">
                        <button className="btn btn-success btn-sm" onClick={() => handleStockIn(row.detail_id)} title="Stock In">+</button>
                        <button className="btn btn-warning btn-sm" onClick={() => handleStockOut(row.detail_id)} title="Stock Out" disabled={!row.Quantity || row.Quantity <= 0}>-</button>
                        <button className="btn btn-info btn-sm" onClick={() => handleViewHistory(row.detail_id)} title="View History">📊</button>
                    </div>
                </div>
            )
        },
        { key: "hsncode", title: "HSN Code", render: (row) => row.hsncode || "-" },
        { key: "gstrate", title: "GST Rate", render: (row) => `${row.gstrate}%` },
        { key: "listprice", title: "List Price", render: (row) => `₹${row.listprice}` },
        { key: "moq", title: "MOQ", render: (row) => row.moq },
        { key: "description", title: "Description", render: (row) => row.description || "-" },
        {
            key: "product_image", title: "Product Image", render: (row) => {
                if (!row.product_image) return "-";
                const imageUrl = `${baseURL}/Uploads/products/${row.product_image}`;
                return (
                    <a href={imageUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }} >
                        <img src={imageUrl} alt={row.product_name || "Product"}
                            style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer', border: '1px solid #ddd', transition: 'transform 0.2s' }}
                            onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
                            onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    </a>
                );
            }
        },
        {
            key: "tech_specs", title: "Tech Specs", render: (row) => {
                if (!row.tech_specs) return "-";
                const fileUrl = `${baseURL}/Uploads/products/${row.tech_specs}`;
                const fileExtension = row.tech_specs.split('.').pop().toLowerCase();
                const previewableDocs = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'];
                if (previewableDocs.includes(fileExtension)) {
                    let previewUrl = '';
                    switch (fileExtension) {
                        case 'pdf': previewUrl = fileUrl; break;
                        case 'doc': case 'docx':
                        case 'ppt': case 'pptx':
                        case 'xls': case 'xlsx':
                            previewUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
                            break;
                        default: previewUrl = fileUrl;
                    }
                    return (
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <a href={previewUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#007bff' }} title="Preview document" > 👁️ Preview </a>
                        </div>
                    );
                }
            }
        },
        {
            key: "Actions", title: "Actions", render: (row) => (
                <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                    <FaEdit style={{ color: "#ff9966", cursor: "pointer" }} title="Edit" onClick={() => handleEdit(row)} />
                    <FaTrash style={{ color: "#ff9966", cursor: "pointer" }} title="Delete" onClick={() => handleDelete(row)} />
                </div>
            )
        }
    ];

    return (
        <div className="CollapseContainer">
            <Navbar onToggleSidebar={setCollapsed} />
            <div className={`Collapse ${collapsed ? "collapsed" : ""}`}>
                <div className='card p-4 responsive-product-container'>
                    <div className="d-flex justify-content-between mb-3 filter-section">
                        <div className="d-flex gap-2 filter-controls">
                            {/* Main Category */}
                            <div className="dropdown-container" ref={mainCategoryRef}>
                                <button className="btn btn-outline-primary dropdown-toggle filter-btn" onClick={() => setShowMainCategoryDropdown(!showMainCategoryDropdown)}>
                                    {selectedMainCategory ? selectedMainCategory.maincategory_name : "Main Category"}
                                </button>
                                {showMainCategoryDropdown && (
                                    <div className="dropdown-menu show">
                                        {mainCategories.map((category) => (
                                            <button key={category.maincategory_id} className="dropdown-item" onClick={() => handleMainCategorySelect(category)}>
                                                {category.maincategory_name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Sub Category */}
                            <div className="dropdown-container" ref={subCategoryRef}>
                                <button className="btn btn-outline-secondary dropdown-toggle filter-btn" onClick={() => setShowSubCategoryDropdown(!showSubCategoryDropdown)} disabled={!selectedMainCategory}>
                                    {selectedSubCategory ? selectedSubCategory.subcategory_name : "Sub Category"}
                                </button>
                                {showSubCategoryDropdown && selectedMainCategory && (
                                    <div className="dropdown-menu show">
                                        {subCategories.map((subCategory) => (
                                            <button key={subCategory.subcategory_id} className="dropdown-item" onClick={() => handleSubCategorySelect(subCategory)}>
                                                {subCategory.subcategory_name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>


                            <div className="dropdown-container" ref={productRef}>
                                <button className="btn btn-outline-info dropdown-toggle filter-btn" onClick={() => setShowProductDropdown(!showProductDropdown)} disabled={!selectedSubCategory}>
                                    {selectedProduct ? selectedProduct.product_name : "Product"}
                                </button>
                                {showProductDropdown && selectedSubCategory && (
                                    <div className="dropdown-menu show">
                                        {products.map((product) => (
                                            <button key={product.product_id} className="dropdown-item" onClick={() => handleProductSelect(product)}>
                                                {product.product_name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {(selectedMainCategory || selectedSubCategory || selectedProduct) && (
                                <button className="btn btn-outline-danger clear-filter-btn" onClick={clearFilters}>Clear Filters</button>
                            )}
                        </div>

                        <div className="d-flex justify-content-end gap-2">
                            <input type="file" accept=".xlsx, .xls" onChange={handleImportExcel} style={{ display: 'none' }} id="excelImport" />
                            <label htmlFor="excelImport" className="btn btn-warning add-lead-btn" style={{ cursor: 'pointer' }}>
                                {loading ? "Uploading..." : "Import Excel"}
                            </label>
                            <button className="btn btn-info add-lead-btn" onClick={handleDownloadTemplate}>Download Template</button>
                            <button className="btn btn-success add-lead-btn" onClick={handleAddUser}>+ Add Product</button>
                        </div>
                    </div>

                    <ReusableTable
                        title="Products List"
                        data={filteredData}
                        columns={columns}
                        initialEntriesPerPage={5}
                        searchPlaceholder="Search products..."
                    />

                    {loading && (
                        <div className="loader-overlay">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Products;